/**
 * Fetch wrapper with caching and single-load guarantee
 *
 * This module provides a fetch wrapper that ensures:
 * - Files are only loaded once (cached after first load)
 * - Concurrent requests to the same resource return the same promise
 * - Persistent caching via IndexedDB across sessions
 * - Timestamp-based cache validation
 * - Cache can be cleared if needed
 */

import {
  clearCache as clearIndexedDBCache,
  getCached,
  isCacheValid,
  isIndexedDBAvailable,
  setCached,
} from "./indexeddb"
import { logger } from "./logger"

interface CacheEntry<T> {
  promise: Promise<T>
  data?: T
  timestamp: number
}

/**
 * Cache for fetch requests
 * Key: URL, Value: Promise or cached data
 */
const fetchCache = new Map<string, CacheEntry<unknown>>()

/**
 * In-flight requests to prevent duplicate fetches
 * Key: URL, Value: Promise
 */
const inflightRequests = new Map<string, Promise<unknown>>()

/**
 * Fetches JSON data from a URL with caching
 *
 * @param url - The URL to fetch
 * @param options - Fetch options
 * @param lastModified - Optional lastModified timestamp from server for cache validation
 * @returns Promise with the parsed JSON data
 */
export async function fetchJson<T = unknown>(
  url: string,
  options?: RequestInit,
  lastModified?: number,
): Promise<T> {
  const cacheKey = url

  // Check memory cache first (fastest)
  const memCached = fetchCache.get(cacheKey)
  if (memCached?.data !== undefined) {
    logger.debug(`Returning memory-cached data for ${url}`, {
      component: "fetch",
    })
    return memCached.data as T
  }

  // Check if there's an in-flight request
  const inflight = inflightRequests.get(cacheKey)
  if (inflight) {
    logger.debug(`Waiting for in-flight request for ${url}`, {
      component: "fetch",
    })
    return inflight as Promise<T>
  }

  // Create new fetch request with IndexedDB support
  logger.debug(`Fetching ${url}`, { component: "fetch" })
  const fetchPromise = (async () => {
    try {
      // Check IndexedDB cache if available
      if (isIndexedDBAvailable() && lastModified !== undefined) {
        const isValid = await isCacheValid(url, lastModified)
        if (isValid) {
          const cached = await getCached<T>(url)
          if (cached) {
            logger.info(`Using IndexedDB cached data for ${url} (valid based on timestamp)`, {
              component: "fetch",
            })

            // Also populate memory cache for faster subsequent access
            fetchCache.set(cacheKey, {
              promise: Promise.resolve(cached.data),
              data: cached.data,
              timestamp: Date.now(),
            })

            return cached.data
          }
        }
      }

      // Fetch from network
      const response = await fetch(url, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...options?.headers,
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch ${url}: ${response.status} ${response.statusText}`)
      }

      const data = (await response.json()) as T

      // Cache in memory
      fetchCache.set(cacheKey, {
        promise: Promise.resolve(data),
        data,
        timestamp: Date.now(),
      })

      // Cache in IndexedDB if available and lastModified is provided
      if (isIndexedDBAvailable() && lastModified !== undefined && lastModified > 0) {
        try {
          await setCached(url, data, lastModified)
          logger.debug(`Cached ${url} in IndexedDB`, { component: "fetch" })
        } catch (error) {
          // Don't fail the request if IndexedDB caching fails
          logger.warn(`Failed to cache ${url} in IndexedDB`, {
            component: "fetch",
            error,
          })
        }
      }

      logger.info(`Successfully fetched and cached ${url}`, {
        component: "fetch",
      })
      return data
    } catch (error) {
      logger.error(`Failed to fetch ${url}`, error, { component: "fetch" })
      throw error
    } finally {
      // Remove from in-flight requests
      inflightRequests.delete(cacheKey)
    }
  })()

  // Store in-flight request
  inflightRequests.set(cacheKey, fetchPromise)

  return fetchPromise
}

/**
 * Clears the fetch cache (both memory and IndexedDB)
 *
 * @param url - Optional URL to clear. If not provided, clears all cache.
 */
export async function clearFetchCache(url?: string): Promise<void> {
  // Clear memory cache
  if (url) {
    fetchCache.delete(url)
    inflightRequests.delete(url)
    logger.debug(`Cleared memory cache for ${url}`, { component: "fetch" })
  } else {
    fetchCache.clear()
    inflightRequests.clear()
    logger.debug("Cleared all memory cache", { component: "fetch" })
  }

  // Clear IndexedDB cache if available
  if (isIndexedDBAvailable()) {
    try {
      await clearIndexedDBCache(url)
      logger.debug(url ? `Cleared IndexedDB cache for ${url}` : "Cleared all IndexedDB cache", {
        component: "fetch",
      })
    } catch (error) {
      logger.warn("Failed to clear IndexedDB cache", {
        component: "fetch",
        error,
      })
    }
  }
}

/**
 * Gets cache statistics
 */
export function getCacheStats() {
  return {
    cacheSize: fetchCache.size,
    inflightRequests: inflightRequests.size,
    cachedUrls: Array.from(fetchCache.keys()),
  }
}

/**
 * Checks if a URL is cached
 */
export function isCached(url: string): boolean {
  const cached = fetchCache.get(url)
  return cached?.data !== undefined
}
